import { useFlag } from '@calendly/feature-check-react'

import { useGetUserQuery } from 'common'
import { useGetMeetingIntelligenceUserSettingsQuery, useNotetakerEntitled } from 'features/meeting_intelligence'
import { isSupportedLocation } from 'features/meetings/utils'
import {
  getMeetingIntelligenceFromEvent,
  getMeetingIntelligenceStatus,
} from 'shared/utils/meeting_intelligence'

import { useGetMeetingQuery } from '../api'
import {
  recapProcessingStateStatusesFlat,
  retryableStatusesFlat
} from '../notetaker_constants'

const isUserEventHost = (event, user) => event?.co_hosts?.some((coHost) => coHost.userUuid === user?.uuid)
  || event?.eventMemberships?.some((organizer) => organizer.kind === 'HOST' && organizer.uuid === user?.uuid)

const addAiNotetakerPolicy = (event, user) => isUserEventHost(event, user)

/**
 * Custom hook that determines if the notetaker toggle should be displayed
 * and what state it should be in.
 *
 * This hook checks multiple conditions:
 * - Meeting intelligence feature flag is enabled
 * - Current user has meeting intelligence enabled in settings
 * - Event is not cancelled
 * - User is the host of the event (for Calendly events)
 * - Meeting platform is supported (Zoom/Google Meet)
 */
export const useGetNotetakerToggleState = (event) => {
  const { data: currentUser } = useGetUserQuery()
  const { isEntitled: isNotetakerEntitled, isLoading: isEntitlementLoading } = useNotetakerEntitled()
  const msTeamsEnabled = useFlag('ms_teams_notetaker')
  const { data: meetingIntelligenceSettings, isLoading: isLoadingSettings } = useGetMeetingIntelligenceUserSettingsQuery(
    {},
    { skip: !isNotetakerEntitled }
  )

  const supportedLocation = isSupportedLocation(event, msTeamsEnabled)

  const { data: meeting, isFetching: isLoadingMeetingData } = useGetMeetingQuery({
    eventId: event?.uuid,
    selectedView: 'scheduled',
    scopeType: 'all_users_and_teams',
  },
  { skip: !supportedLocation || Boolean(event.location) }) // skip unless supported location (Zoom/Google Meet) and no location set

  if (isEntitlementLoading) {
    return { showNotetaker: false,
      notetakerAdded: false,
      trackedMeetingUuid: getMeetingIntelligenceFromEvent(event)?.uuid,
      fetchError: false,
      unsupportedLocation: false,
      showErrorState: false,
      isLoading: true }
  }

  // Return state informing component NOT to show the toggle
  if (!isNotetakerEntitled) {
    return { showNotetaker: false,
      notetakerAdded: false,
      trackedMeetingUuid: getMeetingIntelligenceFromEvent(event)?.uuid,
      fetchError: false,
      unsupportedLocation: false,
      showErrorState: false,
      isLoading: false }
  }

  if (!currentUser) {
    return { showNotetaker: false,
      notetakerAdded: false,
      trackedMeetingUuid: getMeetingIntelligenceFromEvent(event)?.uuid,
      fetchError: true,
      unsupportedLocation: false,
      showErrorState: false,
      isLoading: false }
  }
  const showNotetaker = Boolean(meetingIntelligenceSettings?.miEnabled) // User has MI turned on
      && (addAiNotetakerPolicy(event, currentUser) || (event.kind && event.kind !== 'CALENDLY')) // User is host or event is not CALENDLY
      && !event.cancelled // event is not cancelled

  const isLoading = isLoadingSettings || isLoadingMeetingData

  const showErrorState = Boolean(supportedLocation && meeting?.externalLocation?.errorNotice)

  const meetingIntelligence = getMeetingIntelligenceFromEvent(event)
  const miEnabled = !!meetingIntelligence
  const flattenedStatus = getMeetingIntelligenceStatus(event)?.toLowerCase()
  // Set toggle to "Off" for in-progress meetings that are in retryable statuses
  const retryable = retryableStatusesFlat.includes(flattenedStatus)
  // Set toggle to "Off" for in-progress meetings that are in processing state or completed, since notetaker was removed
  const isRecapProcessingOrCompleted = recapProcessingStateStatusesFlat.includes(flattenedStatus) || flattenedStatus === 'completed'

  const notetakerAdded = miEnabled && !retryable && !isRecapProcessingOrCompleted

  return {
    showNotetaker, // Show notetaker toggle or not
    notetakerAdded, // Toggle position (on/off)
    trackedMeetingUuid: getMeetingIntelligenceFromEvent(event)?.uuid, // Used for delete mutation
    fetchError: false, // Error state (no longer used but kept for API consistency)
    unsupportedLocation: !supportedLocation, // location is not Zoom/Google Meet/Microsoft Teams
    showErrorState, // Indicates a supported location but with an error notice
    isLoading, // Used to display disabled state while loading
    isRecapProcessingOrCompleted, // used to disable toggle in recap processing state
    retryable, // used to enable toggle in retryable states
  }
}
